// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { convertToLocaleId, useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { OptGroup } from 'rc-select';
import React, { ReactElement, useEffect } from 'react';
import { CaseInput, CaseStatusStage, CustomStatusMapType, CustomStatusOutput } from 'thehive-sdk';
import { CaseStatusValues } from '../CaseStatusManage/CaseStatusForm';

import './CaseBulkReopen.scss';

interface CaseBulkReopenProps {
    triggerElem: ReactElement;
    onMultipleEdit: (values: Partial<CaseInput>) => void;
    statusList?: CustomStatusOutput[];
    countItems?: number;
}

export const CaseBulkReopen = ({
    triggerElem,
    onMultipleEdit,
    statusList = [],
    countItems = 0,
}: CaseBulkReopenProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const stageRef = CaseStatusStage.IN_PROGRESS;
    const [caseStatus, setCaseStatus] = React.useState<CustomStatusMapType>({});

    const onFinishHandler = (values: Partial<CaseInput>) => {
        onMultipleEdit(values);
        onResetForm();
    };

    const onResetForm = () => {
        setFieldsTouched(false);
        form.resetFields();
    };

    useEffect(() => {
        if (statusList.length) {
            const statusMap = statusList.reduce((acc, curr) => {
                if (curr.stage !== stageRef) {
                    return acc;
                }
                if (!acc[curr.stage]) {
                    acc[curr.stage] = [];
                }
                acc[curr.stage].push(curr);
                return acc;
            }, {} as CustomStatusMapType);
            setCaseStatus(statusMap);
        }
    }, [statusList, stageRef]);

    return (
        <THDrawer
            title={i(countItems > 1 ? 'case.actions.reopen.label' : 'case.actions.reopen.one-label')}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <div className="case-bulk-status">
                    <THForm<CaseStatusValues>
                        name="case-status-form"
                        onValuesChange={() => {
                            onFormValuesChange();
                            setFieldsTouched(true);
                        }}
                        form={form}
                        onFinish={(values: Partial<CaseInput>) => {
                            onFinishHandler(values);
                            toggleDrawer();
                        }}
                    >
                        <THForm.Layout
                            footer={
                                <FormSubmitButton
                                    onCancel={() => {
                                        onResetForm();
                                        toggleDrawer();
                                    }}
                                    footerText={i('general.confirm')}
                                    disabled={!isValid}
                                    hidden={!fieldsTouched}
                                />
                            }
                        >
                            {statusList && (
                                <THForm.Item name="status" label={i('case.close.form.status.label')}>
                                    <THSelect placeholder={i('case.actions.reopen.placeholder-status')}>
                                        {Object.entries(caseStatus).map(([key, values]) => (
                                            <OptGroup key={key} label={key}>
                                                {values.map(({ value, colour }) => (
                                                    <THSelect.Option key={value} value={value}>
                                                        <CustomColorLabel
                                                            value={i({
                                                                id: `case.data.status.${convertToLocaleId(value)}`,
                                                                defaultMessage: value,
                                                            })}
                                                            color={colour}
                                                        />
                                                    </THSelect.Option>
                                                ))}
                                            </OptGroup>
                                        ))}
                                    </THSelect>
                                </THForm.Item>
                            )}
                        </THForm.Layout>
                    </THForm>
                </div>
            )}
        </THDrawer>
    );
};
